import React, { useEffect, useRef, useState } from "react";

import * as Dialog from "@radix-ui/react-dialog";
import { AlertTriangle, CheckCircle2, Clock, Loader2, X } from "lucide-react";

import { FLOW } from "./flowCopy";
import { aeName } from "./PlanCard";
import { canUseAnyway, planFlow } from "./planFlow";
import { PlanNav } from "./shared";
import { groupChecks, passLabel } from "./UploadProgress";
import {
  apiErrorMessage,
  useAccountPlanActions,
  useMyPlanActivity,
} from "../../Hooks/useAccountPlan";
import type { AccountPlanOverview, PlanFile } from "../../Types/AccountPlan";
import { toast } from "../../Utils/Helper/toast";

/** The Confirm plan box drafts, per upload, in this browser tab only. Storage
 * can be missing or blocked (private mode): then the boxes just start empty. */
interface ConfirmDraft {
  note?: string;
  reason?: string;
}
const readDraft = (key: string): ConfirmDraft => {
  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as ConfirmDraft) : {};
  } catch {
    return {};
  }
};
const writeDraft = (key: string, d: ConfirmDraft) => {
  try {
    if (d.note || d.reason)
      window.sessionStorage.setItem(key, JSON.stringify(d));
    else window.sessionStorage.removeItem(key);
  } catch {
    /* storage unavailable: nothing to keep */
  }
};
const clearDraft = (key: string) => {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    /* storage unavailable */
  }
};

interface Props {
  accountId: string;
  data: AccountPlanOverview;
  /** The upload waiting on Confirm plan (`planFlow(data).toConfirm`). */
  file: PlanFile;
  nav: PlanNav;
  /** Refresh the overview after a confirm or discard. */
  onChanged: () => void;
  /** The briefing started for this upload: the page shows its progress in a
   * pop-up instead of moving to Review briefing (Sushant, 2026-11-03). */
  onBriefingStarted?: (fileId: string) => void;
}

const BTN =
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-lg px-4 py-2 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50 disabled:cursor-not-allowed disabled:opacity-50";

/**
 * Confirm plan (upload flow Phase 2, step 2): the AE checks what ExecBridge read
 * and says it is the account's plan. Nothing is written before this: confirming
 * starts the draft briefing and Briefing Questions. "Upload a different file"
 * (design D23, failure audit G13) sends a new file through the checks; once one
 * becomes the plan, this one is deleted. Everyone else on the team sees the same
 * panel read-only, waiting on the AE.
 */
const ConfirmPlanPanel: React.FC<Props> = ({
  accountId,
  data,
  file,
  nav,
  onChanged,
  onBriefingStarted,
}) => {
  const { confirmPlanVersion, overridePlanVersion, uploadPlanFile } =
    useAccountPlanActions();
  const picker = useRef<HTMLInputElement>(null);
  const { kick } = useMyPlanActivity(false);
  // The pop-up opens only from "Confirm" (Sushant, 2026-11-02): the AE looks
  // through the slides first. It no longer opens on arrival.
  const [open, setOpen] = useState(false);
  // What the AE typed survives leaving the step and coming back (Sushant,
  // 2026-10-31): kept per upload in this tab until it is confirmed or discarded.
  const draftKey = `ap-confirm-draft:${file.id}`;
  const [reason, setReason] = useState(() => readDraft(draftKey).reason ?? "");
  const [note, setNote] = useState(() => readDraft(draftKey).note ?? "");
  useEffect(() => {
    writeDraft(draftKey, { note, reason });
  }, [draftKey, note, reason]);
  const [busy, setBusy] = useState<"confirm" | "override" | "replace" | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);

  const canAct = nav.canAct("AE");
  // The checks stopped it, but the AE may use it with a reason (an account
  // warning, F-UF-E08, design §08): decided here, not only in the upload dialog.
  const anyway = canUseAnyway(file);
  const reasonOk = reason.trim().length >= 3;
  const ae = aeName(data) || "the AE";
  const checks = file.validation?.checks || [];
  const passed = checks.filter(c => c.status === "pass");
  const fails = checks.filter(c => c.status === "fail");
  const warns = checks.filter(c => c.status === "warn");
  // Every flagged item but missing slide pictures (C2) needs the AE's tick before
  // Confirm (UI tour, 2026-11-03: one click confirmed past "the newest year in the
  // plan is 2024" unseen). The server refuses an unticked warning too. On an
  // account check the AE may overrule, the written reason is the decision instead.
  const toTick = anyway ? [] : [...fails, ...warns].filter(c => c.id !== "C2");
  const [ticked, setTicked] = useState<Record<string, boolean>>({});
  const allTicked = toTick.every(c => ticked[c.id]);
  const tick = (id: string, on: boolean) =>
    setTicked(t => ({ ...t, [id]: on }));
  // What the sponsor has now: the newest published version. last_published alone
  // skips the current version, so with v2 published it would name v1.
  const published = planFlow(data).published;
  // Identity by file id, never by number: the publish record's plan number and
  // this upload's internal number are different sequences (D1).
  const stillPublished =
    published && published.fileId !== file.id
      ? `v${published.planVersion}`
      : null;
  // The upload has no number yet; confirming gives it the next one.
  const nextNumber = data.next_plan_version;

  const confirm = async () => {
    setBusy("confirm");
    setError(null);
    try {
      await confirmPlanVersion(
        accountId,
        file.id,
        note,
        toTick.map(c => c.id)
      );
      clearDraft(draftKey);
      kick(); // the header chip and the "briefing ready" pop-up follow it
      onChanged();
      setOpen(false);
      // The run shows in a pop-up on this page; without one, Review briefing shows it.
      if (onBriefingStarted) onBriefingStarted(file.id);
      else nav.go("sponsor", { file: null, slide: null });
    } catch (err) {
      setError(apiErrorMessage(err, "Couldn't confirm the plan."));
      onChanged();
    } finally {
      setBusy(null);
    }
  };

  const useAnyway = async () => {
    setBusy("override");
    setError(null);
    try {
      await overridePlanVersion(accountId, file.id, reason.trim());
      clearDraft(draftKey);
      kick();
      onChanged();
      setOpen(false);
      if (onBriefingStarted) onBriefingStarted(file.id);
      else nav.go("sponsor", { file: null, slide: null });
    } catch (err) {
      setError(apiErrorMessage(err, "Couldn't use this upload."));
      onChanged();
    } finally {
      setBusy(null);
    }
  };

  const replaceWith = async (picked: File) => {
    setBusy("replace");
    setError(null);
    try {
      await uploadPlanFile(accountId, picked);
      clearDraft(draftKey);
      kick();
      toast.success({
        title: `Checking ${picked.name}`,
        message: `It replaces ${file.filename} once you confirm it.`,
      });
      onChanged();
      nav.go("plan", { file: null, slide: null });
    } catch (err) {
      setError(apiErrorMessage(err, "Couldn't upload that file."));
      onChanged();
    } finally {
      setBusy(null);
    }
  };

  // A generic heading (Sushant, 2026-11-02): naming the first problem read as if
  // it were the only one. The checks themselves are listed underneath.
  const flagged = [...fails, ...warns];
  const flaggedCount = flagged.length;
  const title = flaggedCount
    ? `${flaggedCount} item${flaggedCount === 1 ? "" : "s"} to review before confirming`
    : "Review what ExecBridge read";
  const pages = file.page_count
    ? ` · ${file.page_count} ${file.document_type === "pdf" ? "page" : "slide"}${file.page_count === 1 ? "" : "s"}`
    : "";

  /** One flagged check: what's wrong, why, and (for the AE) the tick it needs. */
  const problemItem = (c: (typeof checks)[number], where: "bar" | "pop") => {
    const needsTick = canAct && toTick.some(t => t.id === c.id);
    return (
      <li
        key={c.id}
        data-testid={
          where === "pop" ? "confirm-check-problem" : "confirm-bar-problem"
        }
        className="flex items-start gap-2 text-sm"
      >
        <AlertTriangle
          className={`mt-0.5 h-4 w-4 shrink-0 ${
            c.status === "fail" ? "text-rose-600" : "text-amber-600"
          }`}
          aria-hidden
        />
        <span className="min-w-0 flex-1">
          <span className="font-semibold text-gray-900">
            {c.problem || c.title}
          </span>
          {c.detail && (
            <span className="block text-[13px] text-gray-600">{c.detail}</span>
          )}
          {needsTick && (
            <label className="mt-1 inline-flex cursor-pointer items-center gap-2 text-[13px] font-medium text-gray-800">
              <input
                type="checkbox"
                checked={!!ticked[c.id]}
                onChange={e => tick(c.id, e.target.checked)}
                data-testid={`confirm-ack-${c.id}`}
                aria-label={`${FLOW.ackItem}: ${c.problem || c.title}`}
                className="h-4 w-4 rounded border-gray-300 text-[#974ae4] focus:ring-[#974ae4]/50"
              />
              {FLOW.ackItem}
            </label>
          )}
        </span>
      </li>
    );
  };

  const body = (
    <>
      <p className="text-sm text-gray-500">
        {file.filename}
        {pages}
      </p>

      {!!checks.length && (
        <ul aria-label="Plan checks" className="mt-4 space-y-2">
          {[...fails, ...warns].map(c => problemItem(c, "pop"))}
          {!!passed.length && (
            // Passed checks show as four groups, each opening to its checks
            // (Sushant, 2026-11-03): the AE needs the exceptions, not 22 lines.
            <li className="text-[13px] text-gray-700">
              <div>
                <div className="flex items-center gap-2">
                  <CheckCircle2
                    className="h-4 w-4 shrink-0 text-emerald-600"
                    aria-hidden
                  />
                  <span className="font-medium text-gray-900">
                    {fails.length || warns.length
                      ? `${passed.length} other check${passed.length === 1 ? "" : "s"} passed`
                      : passed.length === 1
                        ? "1 check passed"
                        : `All ${passed.length} checks passed`}
                  </span>
                </div>
                <ul
                  className="ml-6 mt-1.5 space-y-1 text-gray-600"
                  data-testid="check-groups"
                >
                  {groupChecks(passed).map(g => (
                    <li key={g.key}>
                      <details>
                        <summary className="flex cursor-pointer list-none items-center gap-2">
                          <CheckCircle2
                            className="h-3.5 w-3.5 shrink-0 text-emerald-600"
                            aria-hidden
                          />
                          <span className="text-gray-800">{g.label}</span>
                          <span className="text-gray-400">
                            · {g.checks.length}
                          </span>
                        </summary>
                        <ul className="ml-6 mt-1 space-y-0.5 text-gray-500">
                          {g.checks.map(c => (
                            <li key={c.id}>
                              {passLabel(c)}
                              {c.detail && (
                                <span className="text-gray-400">
                                  {" "}
                                  · {c.detail}
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </details>
                    </li>
                  ))}
                </ul>
              </div>
            </li>
          )}
        </ul>
      )}

      {!canAct && (
        <div
          role="status"
          className="mt-4 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900"
        >
          <Clock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>
            <span className="font-semibold">
              Waiting on {ae} to confirm the plan.
            </span>{" "}
            Only the AE confirms. You&apos;re notified when the draft briefing
            is ready for your sections.
          </span>
        </div>
      )}

      {canAct && anyway && (
        <label className="mt-4 block text-sm font-medium text-gray-800">
          Why is this the right plan?
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            rows={2}
            maxLength={500}
            data-testid="confirm-plan-reason"
            placeholder="For example: it's the parent company's deck"
            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-normal text-gray-900 focus:border-[#974ae4] focus:outline-none focus:ring-2 focus:ring-[#974ae4]/30"
          />
          <span className="mt-1 block text-xs font-normal text-gray-500">
            The reason stays on the version, and the CSM is told.
          </span>
        </label>
      )}

      {canAct && !anyway && (
        <label className="mt-4 block text-sm font-medium text-gray-800">
          {FLOW.confirmNoteLabel}
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            rows={2}
            maxLength={1000}
            data-testid="confirm-plan-note"
            placeholder={FLOW.confirmNotePlaceholder}
            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-normal text-gray-900 focus:border-[#974ae4] focus:outline-none focus:ring-2 focus:ring-[#974ae4]/30"
          />
          <span className="mt-1 block text-xs font-normal text-gray-500">
            {FLOW.confirmNoteHelp}
          </span>
        </label>
      )}

      {canAct && (
        <div className="mt-5 border-t border-gray-100 pt-4">
          {(nextNumber != null || stillPublished) && (
            <p className="mb-3 text-xs text-gray-500">
              {nextNumber != null
                ? `Confirming saves this upload as version ${nextNumber}.`
                : ""}
              {stillPublished
                ? ` The sponsor keeps reading ${stillPublished} until the new briefing is published.`
                : ""}
            </p>
          )}
          {!allTicked && (
            <p
              data-testid="confirm-tick-hint"
              className="mb-3 text-xs font-medium text-amber-800"
            >
              {FLOW.tickToConfirm}
            </p>
          )}
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              data-testid="confirm-plan-replace"
              onClick={() => picker.current?.click()}
              disabled={!!busy}
              // Possibly another account's deck: a different file is the likely fix.
              className={`${BTN} ${
                anyway
                  ? "order-last bg-[#974ae4] text-white hover:bg-[#8538d4]"
                  : "border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              {busy === "replace" && (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              )}
              {anyway ? FLOW.uploadDifferent : FLOW.replaceFile}
            </button>
            {anyway ? (
              <button
                type="button"
                data-testid="confirm-plan-anyway"
                onClick={() => void useAnyway()}
                disabled={!!busy || !reasonOk}
                className={`${BTN} border border-gray-200 bg-white text-gray-700 hover:bg-gray-50`}
              >
                {busy === "override" && (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                )}
                {FLOW.useAnyway}
              </button>
            ) : (
              <button
                type="button"
                data-testid="confirm-plan-yes"
                onClick={() => void confirm()}
                disabled={!!busy || !allTicked}
                className={`${BTN} bg-[#974ae4] text-white hover:bg-[#8538d4]`}
              >
                {busy === "confirm" && (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                )}
                {FLOW.confirmAndWrite}
              </button>
            )}
          </div>
        </div>
      )}

      {error && (
        <p role="alert" className="mt-3 text-sm text-red-600">
          {error}
        </p>
      )}
    </>
  );

  // Only the AE decides, so only the AE gets the pop-up (product review
  // 2026-11-01); everyone else sees the same content read-only on the page.
  if (!canAct)
    return (
      <section
        data-testid="confirm-plan"
        aria-labelledby="confirm-plan-title"
        className="mb-6 rounded-xl border border-gray-200 bg-white p-5 shadow-sm"
      >
        <h3 id="confirm-plan-title" className="text-lg font-bold text-gray-900">
          {title}
        </h3>
        {body}
      </section>
    );

  return (
    <>
      {/* One picker for the bar's and the pop-up's "Upload a different file". */}
      <input
        ref={picker}
        type="file"
        accept=".pdf,.pptx"
        className="hidden"
        data-testid="confirm-plan-replace-input"
        onChange={e => {
          const picked = e.target.files?.[0];
          e.target.value = "";
          if (picked) void replaceWith(picked);
        }}
      />
      {/* Closed to look through the slides: a bar reopens it. */}
      <div
        data-testid="confirm-plan-bar"
        className={`mb-6 rounded-xl border px-4 py-3 text-sm ${
          flagged.length
            ? "border-amber-200 bg-amber-50/40"
            : "border-purple-200 bg-purple-50/50"
        }`}
      >
        {/* Each flagged item is on the page, above Confirm (UI tour, 2026-11-03):
            the AE reads and ticks it here, not only inside the pop-up. */}
        {!!flagged.length && (
          <ul
            aria-label="Items to review"
            className="mb-3 space-y-2 border-b border-amber-100 pb-3"
          >
            {flagged.map(c => problemItem(c, "bar"))}
          </ul>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <span className="min-w-0 flex-1">
            <span className="font-semibold text-gray-900">{title}</span>
            <span className="text-gray-600">
              {" "}
              · The briefing is written once you confirm.
            </span>
            {!allTicked && (
              <span className="block text-xs font-medium text-amber-800">
                {FLOW.tickToConfirm}
              </span>
            )}
          </span>
          {anyway && (
            // Possibly another account's deck: a different file is the likely fix,
            // so it is the filled button; keeping this one needs a reason.
            <button
              type="button"
              data-testid="confirm-plan-replace-bar"
              onClick={() => picker.current?.click()}
              disabled={!!busy}
              className={`${BTN} order-last bg-[#974ae4] text-white hover:bg-[#8538d4]`}
            >
              {busy === "replace" && (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              )}
              {FLOW.uploadDifferent}
            </button>
          )}
          <button
            type="button"
            data-testid="confirm-plan-open"
            onClick={() => setOpen(true)}
            disabled={!allTicked}
            title={allTicked ? undefined : FLOW.tickToConfirm}
            className={`${BTN} ${
              anyway
                ? "border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                : "bg-[#974ae4] text-white hover:bg-[#8538d4]"
            }`}
          >
            {anyway ? FLOW.rightPlan : FLOW.reviewAndConfirm}
          </button>
        </div>
        {error && !open && (
          <p role="alert" className="mt-2 text-sm text-red-600">
            {error}
          </p>
        )}
      </div>
      <Dialog.Root open={open} onOpenChange={setOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-gray-900/30" />
          <Dialog.Content
            data-testid="confirm-plan"
            aria-describedby={undefined}
            className="fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-[calc(100%-32px)] max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-xl border border-gray-200 bg-white p-5 shadow-xl focus:outline-none"
          >
            <div className="flex items-start justify-between gap-3">
              <Dialog.Title className="text-lg font-bold text-gray-900">
                {title}
              </Dialog.Title>
              <Dialog.Close
                aria-label="Close"
                className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
              >
                <X className="h-4 w-4" aria-hidden />
              </Dialog.Close>
            </div>
            {body}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
};

export default ConfirmPlanPanel;
